import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Tooltip } from '../../components/ui';

/**
 * Small presentational pieces shared by the role dashboards.
 *
 * They exist so every preset reads the same way: a KPI is a number you can tap
 * through, a widget is a titled card with its own loading, empty and error
 * states, and a queue is a stack of rows that works as well at 360px as it does
 * on a wide screen. Nothing here fetches; each widget owns its own data.
 */

/* -------------------------------------------------------------------------- */
/* KPI strip                                                                   */
/* -------------------------------------------------------------------------- */

export type KpiTone = 'default' | 'good' | 'warn' | 'bad';

const KPI_TONE: Record<KpiTone, string> = {
  default: 'border-l-brand',
  good: 'border-l-brand',
  warn: 'border-l-[color:var(--gs-hazard)]',
  bad: 'border-l-red-400',
};

const KPI_VALUE_TONE: Record<KpiTone, string> = {
  default: 'text-ink',
  good: 'text-brand-deep',
  warn: 'text-ink',
  bad: 'text-red-700',
};

export interface Kpi {
  label: string;
  value: string;
  hint?: string;
  tone?: KpiTone;
  /** Where the number leads, when there is a screen that shows the detail. */
  to?: string;
  tooltip?: string;
}

export function KpiStrip({ items }: { items: Kpi[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
      {items.map((item) => (
        <KpiTile key={item.label} kpi={item} />
      ))}
    </div>
  );
}

function KpiTile({ kpi }: { kpi: Kpi }) {
  const tone = kpi.tone ?? 'default';

  const body = (
    <>
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink-muted">
          {kpi.label}
        </p>
        {kpi.tooltip ? <Tooltip text={kpi.tooltip} subject={kpi.label} /> : null}
      </div>
      <p className={`mt-2 text-xl font-semibold sm:text-2xl ${KPI_VALUE_TONE[tone]}`}>
        {kpi.value}
      </p>
      {kpi.hint ? <p className="mt-0.5 text-xs text-ink-muted">{kpi.hint}</p> : null}
    </>
  );

  const className = `block rounded-md border border-surface-muted border-l-4 bg-white p-3 shadow-sm sm:p-4 ${KPI_TONE[tone]} ${
    kpi.to ? 'transition hover:border-brand' : ''
  }`;

  if (kpi.to) {
    return (
      <Link to={kpi.to} className={className} aria-label={`${kpi.label}: ${kpi.value}`}>
        {body}
      </Link>
    );
  }

  return <div className={className}>{body}</div>;
}

/* -------------------------------------------------------------------------- */
/* Widget card                                                                 */
/* -------------------------------------------------------------------------- */

export function WidgetCard({
  title,
  description,
  to,
  toLabel = 'View all',
  badge,
  children,
  className = '',
}: {
  title: string;
  description?: string;
  to?: string;
  toLabel?: string;
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={`flex h-full flex-col ${className}`}>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-base font-bold text-ink">
              {title}
              {badge}
            </h2>
            {description ? (
              <p className="mt-1 text-sm font-light text-ink-body">{description}</p>
            ) : null}
          </div>
          {to ? (
            <Link
              to={to}
              className="shrink-0 text-sm font-semibold text-brand-deep hover:underline"
            >
              {toLabel} →
            </Link>
          ) : null}
        </div>
        <div className="mt-3">{children}</div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Widget states                                                               */
/* -------------------------------------------------------------------------- */

export function WidgetSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2 py-1" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-10 animate-pulse rounded-sm bg-surface" />
      ))}
    </div>
  );
}

export function WidgetError({ label, onRetry }: { label: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <span>{label}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 text-xs font-bold uppercase tracking-wide underline"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function WidgetEmpty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-ink-muted">{children}</p>;
}

/* -------------------------------------------------------------------------- */
/* Queues — stacked rows, never a horizontally scrolling table                 */
/* -------------------------------------------------------------------------- */

export function WidgetList({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-surface-muted">{children}</ul>;
}

/**
 * A row that leads somewhere when `to` is set, and is a plain row when it does
 * not. A row with its own action button should not be a link as well — nesting
 * controls inside one another is a trap for touch and screen readers — so those
 * call sites pass no `to` and put a small text link on the label instead.
 */
export function WidgetRow({
  children,
  to,
  className = '',
}: {
  children: ReactNode;
  to?: string;
  className?: string;
}) {
  if (to) {
    return (
      <li>
        <Link
          to={to}
          className={`-mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition hover:bg-brand-soft/40 ${className}`}
        >
          {children}
        </Link>
      </li>
    );
  }

  return <li className={`flex items-center gap-3 py-3 ${className}`}>{children}</li>;
}

/** The two-line name block used at the left of most rows. */
export function RowTitle({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-ink">{title}</p>
      {subtitle ? <p className="truncate text-xs text-ink-muted">{subtitle}</p> : null}
    </div>
  );
}

/** A right-aligned figure, optionally with a caption under it. */
export function RowFigure({ value, caption }: { value: ReactNode; caption?: ReactNode }) {
  return (
    <div className="shrink-0 text-right">
      <p className="whitespace-nowrap text-sm font-semibold text-ink">{value}</p>
      {caption ? <p className="whitespace-nowrap text-xs text-ink-muted">{caption}</p> : null}
    </div>
  );
}
